import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Row,
  Col,
  Table,
  Label,
  Input,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import axios from "axios";
import Header from "components/Headers/Header";
import Loader from "components/Loader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
];

const AnalyticsReport = () => {
  const [data, setData] = useState([]);
  const [dimension, setDimension] = useState("sessionCampaignName");
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState("sessions");
  const [sortDirection, setSortDirection] = useState("desc");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 🔄 Buscar dados (padrão = ontem)
  const fetchAnalytics = async (custom = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("⚠️ Nenhum token encontrado no localStorage!");
        setLoading(false);
        return;
      }

      const res = await axios.post(
        `${API_URL}/analytics`,
        {
          utm: dimension,
          startDate: custom ? startDate || "yesterday" : "yesterday",
          endDate: custom ? endDate || "yesterday" : "yesterday",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setData(res.data);
      setCurrentPage(1);
    } catch (err) {
      console.error("❌ Erro ao buscar relatório:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🚀 Busca inicial (ontem)
  useEffect(() => {
    fetchAnalytics(false);
  }, [dimension]);

  // 🧭 Extrai sigla do país do subdomínio
  const extractCountryCode = (host) => {
    if (!host) return "";
    const parts = host.split(".");
    return parts.length > 2 ? parts[0] : "global";
  };

  // 📊 Dados para gráfico de barras
  const uniqueHosts = [...new Set(data.map((item) => item.host))];
  const chartData = uniqueHosts.map((host) => {
    const hostItems = data.filter((item) => item.host === host);
    const totalSessions = hostItems.reduce(
      (acc, item) => acc + parseInt(item.sessions || 0),
      0
    );
    return { country: extractCountryCode(host), sessions: totalSessions };
  });

  // 🥧 Dados para gráfico de pizza
  const utmData = [...new Set(data.map((item) => item.utmValue))].map((utm) => {
    const utmItems = data.filter((item) => item.utmValue === utm);
    const totalUsers = utmItems.reduce(
      (acc, item) => acc + parseInt(item.activeUsers || 0),
      0
    );
    const formatted =
      utm && utm.length > 25 ? utm.substring(0, 25) + "..." : utm || "(vazio)";
    return { name: formatted, value: totalUsers };
  });

  // 🔧 comparator para número, data (YYYY-MM-DD) e string
  const isISODate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
  const toNumber = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1); // volta para primeira página ao ordenar
  };

  const sortedData = [...data].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];

    // número
    if (!isNaN(toNumber(valA)) && !isNaN(toNumber(valB))) {
      valA = toNumber(valA);
      valB = toNumber(valB);
      return sortDirection === "asc" ? valA - valB : valB - valA;
    }

    // data ISO YYYY-MM-DD
    if (isISODate(valA) && isISODate(valB)) {
      return sortDirection === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }

    // string (case-insensitive)
    valA = (valA ?? "").toString().toLowerCase();
    valB = (valB ?? "").toString().toLowerCase();
    return sortDirection === "asc"
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  });

  // 🔢 Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        {/* 🔍 Filtro */}
        <Row className="mb-3">
          <Col md="3">
            <Label className="font-weight-bold text-dark">Início:</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
          <Col md="3">
            <Label className="font-weight-bold text-dark">Fim:</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
          <Col md="3">
            <Label className="font-weight-bold text-dark">Dimensão UTM:</Label>
            <Input
              type="select"
              value={dimension}
              onChange={(e) => setDimension(e.target.value)}
            >
              <option value="sessionSource">utm_source</option>
              <option value="sessionMedium">utm_medium</option>
              <option value="sessionCampaignName">utm_campaign</option>
              <option value="sessionContent">utm_content</option>
            </Input>
          </Col>
          <Col
            md="3"
            className="d-flex align-items-end justify-content-start"
          >
            <Button color="primary" onClick={() => fetchAnalytics(true)}>
              🔍 Consultar
            </Button>
          </Col>
        </Row>

        {/* 📊 Gráficos */}
        <Row>
          <Col xl="6">
            <Card className="shadow" style={{ minHeight: 320 }}>
              <CardHeader>
                <h3 className="mb-0">📊 Sessões por País</h3>
              </CardHeader>
              <CardBody className="d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                {loading ? (
                  <Loader />
                ) : chartData.length === 0 ? (
                  <p className="text-center text-muted">Sem dados disponíveis.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="country" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="shadow" style={{ minHeight: 320 }}>
              <CardHeader>
                <h3 className="mb-0">📈 Distribuição por UTM</h3>
              </CardHeader>

              <CardBody
                className={`${loading
                    ? "d-flex justify-content-center align-items-center"
                    : "d-flex flex-row"
                  }`}
                style={{ height: 250 }}
              >
                {loading ? (
                  <Loader />
                ) : utmData.length === 0 ? (
                  <p className="text-center text-muted">Sem dados disponíveis.</p>
                ) : (
                  <>
                    <ResponsiveContainer width="60%" height={250}>
                      <PieChart>
                        <Pie
                          data={utmData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                        >
                          {utmData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>

                    <div
                      style={{ width: "40%", overflowY: "auto", maxHeight: 250 }}
                    >
                      <Table borderless size="sm">
                        <tbody>
                          {utmData.map((entry, index) => (
                            <tr key={index}>
                              <td>
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 10,
                                    height: 10,
                                    backgroundColor:
                                      COLORS[index % COLORS.length],
                                    marginRight: 8,
                                    borderRadius: "50%",
                                  }}
                                ></span>
                                {entry.name}
                              </td>
                              <td className="text-right">{entry.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 📋 Tabela */}
        <Row className="mt-4">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">📋 Detalhamento por UTM</h3>
              </CardHeader>

              {loading ? (
                <Loader />
              ) : (
                <>
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        {[
                          { key: "date", label: "🗓 Data" },
                          { key: "utmValue", label: "🔖 UTM" },
                          { key: "host", label: "🌐 Domínio" },
                          { key: "activeUsers", label: "👥 Usuários" },
                          { key: "avgSessionDuration", label: "⏱ Duração Média" },
                          { key: "sessions", label: "📍 Sessões" },
                          { key: "events", label: "⚡ Eventos" },
                        ].map((col) => (
                          <th
                            key={col.key}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            onClick={() => handleSort(col.key)}
                            title={`Ordenar por ${col.label.replace(/^[^\s]+\s/, "")}`}
                          >
                            {col.label}{" "}
                            {sortColumn === col.key ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.date}</td>
                          <td>{row.utmValue}</td>
                          <td>{row.host}</td>
                          <td>{row.activeUsers}</td>
                          <td>{parseFloat(row.avgSessionDuration).toFixed(1)}s</td>
                          <td>{row.sessions}</td>
                          <td>{row.events}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* 📄 Paginação */}
                  <CardFooter className="py-4">
                    <Pagination className="pagination justify-content-end mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1)
                              setCurrentPage(currentPage - 1);
                          }}
                        >
                          <i className="fas fa-angle-left" />
                        </PaginationLink>
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <PaginationItem
                          key={i}
                          active={currentPage === i + 1}
                        >
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(i + 1);
                            }}
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages)
                              setCurrentPage(currentPage + 1);
                          }}
                        >
                          <i className="fas fa-angle-right" />
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </CardFooter>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AnalyticsReport;